// Client risk level (Grado de Riesgo): low / medium (default) / high.
// High is automatic for a foreign politically exposed person (PEP) and for a
// non-resident client from a high-risk jurisdiction; in every other case the
// compliance officer decides.
import type { Client, RiskLevel } from "./types";

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[.,]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const MEXICO_ALIASES = new Set(["mexico", "mx", "mex", "estados unidos mexicanos"]);

/**
 * FATF "high-risk jurisdictions subject to a call for action" (black list).
 * Review after every FATF plenary (February, June, October). `name` is the
 * user-facing Spanish name; aliases are matched after normalization.
 */
export const HIGH_RISK_JURISDICTIONS = [
  {
    name: "Corea del Norte",
    aliases: [
      "corea del norte",
      "republica popular democratica de corea",
      "north korea",
      "dprk",
      "democratic people's republic of korea",
    ],
  },
  { name: "Irán", aliases: ["iran", "republica islamica de iran", "islamic republic of iran"] },
  { name: "Myanmar", aliases: ["myanmar", "birmania", "burma"] },
] as const;

const HIGH_RISK_ALIASES = new Set<string>(HIGH_RISK_JURISDICTIONS.flatMap((j) => j.aliases));

export function isForeign(client: Pick<Client, "type"> & { country?: string | null }): boolean {
  if (client.type === "foreign_individual" || client.type === "foreign_legal_entity") return true;
  return !!client.country && !MEXICO_ALIASES.has(normalize(client.country));
}

export const isHighRiskJurisdiction = (country: string | null | undefined) =>
  !!country && HIGH_RISK_ALIASES.has(normalize(country));

export type AutomaticRiskReason = "foreign_pep" | "high_risk_jurisdiction";

type RiskInput = Pick<Client, "type" | "pep"> & { country?: string | null };

/** Why a client is automatically high risk, or `null` if the officer decides. */
export function automaticRiskReason(client: RiskInput): AutomaticRiskReason | null {
  const foreign = isForeign(client);
  if (client.pep && foreign) return "foreign_pep";
  if (foreign && isHighRiskJurisdiction(client.country)) return "high_risk_jurisdiction";
  return null;
}

/** The level that applies: automatic high risk overrides the manual choice. */
export const effectiveRiskLevel = (client: RiskInput & Pick<Client, "riskLevel">): RiskLevel =>
  automaticRiskReason(client) ? "high" : client.riskLevel;
